import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import { persistor } from '@/shared/store';
import { setUser, clearUser } from '@/shared/store/slices/userSlice';
import {
  setAuthAuthenticated,
  setAuthUnauthenticated,
  setAuthChecking,
  setPermissions,
  setApiKey,
  clearApiKey,
  initializeAuth,
  selectIsAuthenticated,
  selectAuthStatus,
  selectIsAuthChecking,
  selectIsAuthInitialized,
  selectPermissionsLoaded,
} from '@/shared/store/slices/authSlice';
import {
  setAuthData,
  clearAuthData,
  getUser,
  isAuthenticated as checkUserExists,
  updateStoredUser,
} from '@/shared/utils/storage';
import { clearAuthCookies } from '@/shared/utils/sessionCleanup';
import {
  useLoginMutation,
  useLogoutMutation,
  useVerifyOtpMutation,
  useLazyGetMeQuery,
  type LoginPayload,
  type User,
  type UserProfileResponse,
} from '@/features/auth/api/authApi';
import { useCallback, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import type { PermissionCode } from '@/features/auth/permissions';

/**
 * Shared across every useAuth() instance so the boot-time auth check (and its
 * /api/auth/user/me call) runs once even though several components mount the hook.
 */
let authCheckInFlight: Promise<boolean> | null = null;

/**
 * Central authentication hook
 * - Handles login
 * - Handles OTP verification
 * - Manages auth state, tokens (via HttpOnly cookies), permissions
 */
export const useAuth = () => {
  const dispatch = useAppDispatch();

  const [loginMutation, { isLoading: isLoginLoading, error }] =
    useLoginMutation();
  const [logoutMutation] = useLogoutMutation();
  const [verifyOtpMutation, { isLoading: isOtpLoading }] =
    useVerifyOtpMutation();

  // Auth state selectors
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isChecking = useAppSelector(selectIsAuthChecking);
  const authStatus = useAppSelector(selectAuthStatus);
  const isInitialized = useAppSelector(selectIsAuthInitialized);
  const currentUser = useAppSelector((state) => state.user.currentUser);

  const [triggerGetMe] = useLazyGetMeQuery();

  // Mirror permissionsLoaded into a ref so the failure paths below can read the
  // current value without being re-created on every permission change.
  const permissionsLoaded = useAppSelector(selectPermissionsLoaded);
  const permissionsLoadedRef = useRef(permissionsLoaded);

  useEffect(() => {
    permissionsLoadedRef.current = permissionsLoaded;
  }, [permissionsLoaded]);

  /**
   * Fetch user profile and permissions from /api/auth/user/me
   * Uses HttpOnly cookies for authentication (automatic browser behavior)
   */
  const fetchUserProfile = useCallback(
    (): Promise<UserProfileResponse> => triggerGetMe().unwrap(),
    [triggerGetMe]
  );

  /**
   * Apply a /user/me response to Redux and local storage.
   * Existing stored fields (e.g. loginMethodCode) are preserved.
   */
  const applyUserProfile = useCallback(
    (profile: UserProfileResponse, userId: string) => {
      console.log('[useAuth] Applying user profile:', profile);

      dispatch(setPermissions(profile.permissions as PermissionCode[]));

      // Extract and store apiKey from user/me endpoint
      if (profile.apiKey) {
        console.log('[useAuth] API key retrieved from profile');
        dispatch(setApiKey(profile.apiKey));
      }

      const userFromProfile: User = {
        ...(getUser<User>() ?? {}),
        id: userId,
        email: profile.email,
        name: profile.name,
        role: profile.role,
        enterpriseId: profile.enterpriseId,
      };

      console.log('[useAuth] Setting user from profile:', userFromProfile);
      dispatch(setUser(userFromProfile));
      updateStoredUser(userFromProfile as unknown as Record<string, unknown>);

      return userFromProfile;
    },
    [dispatch]
  );

  /**
   * Never leave the app authenticated with permissionsLoaded === false:
   * ProtectedRoute would wait forever for permissions that are not coming.
   * Marking them loaded (empty) turns the hang into a /home redirect.
   */
  const resolvePermissionsAfterFailure = useCallback(() => {
    if (!permissionsLoadedRef.current) {
      console.log('[useAuth] Resolving permissions after failure');
      dispatch(setPermissions([]));
    }
  }, [dispatch]);

  /**
   * Sync auth state with stored user data
   * Tokens are managed by server via HttpOnly cookies
   * Token validity is checked by the server (not by client)
   */
  const checkAuthStatus = useCallback(async () => {
    // Several components mount useAuth(); only run the check (and its
    // /user/me call) once per boot.
    if (authCheckInFlight) {
      return authCheckInFlight;
    }

    const runCheck = async (): Promise<boolean> => {
      dispatch(setAuthChecking());

      try {
        console.log('[useAuth] Checking auth status...');
        const storedUser = getUser<User>();
        console.log('[useAuth] Stored user found:', !!storedUser);

        if (storedUser && checkUserExists()) {
          console.log('[useAuth] User is authenticated');
          if (!currentUser) {
            dispatch(setUser(storedUser));
          }

          // User exists, auth is valid
          // Refetch profile and permissions to ensure session is still valid
          try {
            const profileResponse = await fetchUserProfile();
            applyUserProfile(profileResponse, storedUser.id);
          } catch (profileError) {
            console.error(
              '[useAuth] Failed to fetch user profile on session restore:',
              profileError
            );
            // If fetching profile fails, user is not authenticated
            dispatch(setAuthUnauthenticated());
            return false;
          }

          dispatch(setAuthAuthenticated());
          return true;
        }

        console.log('[useAuth] User is not authenticated');
        dispatch(setAuthUnauthenticated());
        return false;
      } catch (err) {
        console.error('[useAuth] Auth check failed:', err);
        dispatch(setAuthUnauthenticated());
        return false;
      }
    };

    authCheckInFlight = runCheck();

    try {
      return await authCheckInFlight;
    } finally {
      authCheckInFlight = null;
    }
  }, [dispatch, currentUser, fetchUserProfile, applyUserProfile]);

  /**
   * LOGIN (STEP 1)
   * - May return OTP requirement OR success
   * - On success, fetches user profile and permissions from /api/auth/user/me
   *
   * Notes:
   * - Tokens are now in HttpOnly cookies set by server (Set-Cookie headers)
   * - Browser automatically sends cookies with all requests
   * - Response body contains ONLY user data (no tokens)
   */
  const login = async (credentials: LoginPayload) => {
    console.log('[useAuth] Login attempt for user:', credentials.username);
    dispatch(setAuthChecking());

    try {
      const response = await loginMutation(credentials).unwrap();
      console.log('[useAuth] Login response type:', response.type);

      switch (response.type) {
        case 'otp':
        case 'emailVerification':
        case 'esimProvision':
          console.log(
            '[useAuth] Additional authentication required:',
            response.type
          );
          // Reset auth state because user is NOT authenticated yet
          dispatch(setAuthUnauthenticated());
          return response;

        case 'login': {
          setAuthData({
            user: response.user as Record<string, unknown>,
            expiresIn: response.expiresIn,
            refreshExpiresIn: response.refreshExpiresIn,
          });

          // Fetch user profile and permissions from /api/auth/user/me
          try {
            const profileResponse = await fetchUserProfile();
            console.log(
              '[useAuth] Fetched permissions:',
              profileResponse.permissions
            );
            applyUserProfile(profileResponse, response.user.id);
          } catch (profileError) {
            console.error(
              '[useAuth] Failed to fetch user profile:',
              profileError
            );
            // Fallback: use minimal data from login response if user/me fails
            dispatch(
              setUser({
                id: response.user.id,
                email: '',
                name: '',
                role: '',
                loginMethodCode: response.user.loginMethodCode,
                roleCode: response.user.roleCode,
              })
            );
            resolvePermissionsAfterFailure();
          }

          dispatch(setAuthAuthenticated());
          console.log('[useAuth] Login completed successfully');
          return response;
        }
      }
    } catch (err) {
      console.error('[useAuth] Login failed:', err);
      dispatch(setAuthUnauthenticated());
      throw err;
    }
  };

  /**
   * OTP VERIFICATION (STEP 2)
   * - Fetches user profile and permissions from /api/auth/user/me after OTP verification
   *
   * Notes:
   * - Tokens are now in HttpOnly cookies set by server
   * - Response body contains ONLY user data
   */
  const verifyOtp = async (otpId: string, code: string, username: string) => {
    console.log('[useAuth] Verifying OTP for user:', username);
    dispatch(setAuthChecking());

    try {
      const response = await verifyOtpMutation({
        otpId,
        code,
        username,
      }).unwrap();

      // OTP verified → full login
      setAuthData({
        user: response.user as Record<string, unknown>,
        expiresIn: response.expiresIn,
        refreshExpiresIn: response.refreshExpiresIn,
      });

      // Fetch user profile and permissions from /api/auth/user/me
      try {
        const profileResponse = await fetchUserProfile();
        console.log(
          '[useAuth] Fetched permissions after OTP:',
          profileResponse.permissions
        );
        applyUserProfile(profileResponse, response.user.id);
      } catch (profileError) {
        console.error(
          '[useAuth] Failed to fetch user profile (OTP):',
          profileError
        );
        // Fallback: use minimal data from OTP response if user/me fails
        dispatch(
          setUser({
            id: response.user.id,
            email: '',
            name: '',
            role: '',
            loginMethodCode: response.user.loginMethodCode,
            roleCode: response.user.roleCode,
          })
        );
        resolvePermissionsAfterFailure();
      }

      dispatch(setAuthAuthenticated());
      console.log('[useAuth] OTP verification completed successfully');

      return response;
    } catch (err) {
      console.error('[useAuth] OTP verification failed:', err);
      dispatch(setAuthUnauthenticated());
      throw err;
    }
  };

  /**
   * LOGOUT
   * - Calls backend logout endpoint to clear HttpOnly cookies
   * - Clears client-side data
   * - Redirects to login page
   */
  const logout = async (
    redirectPath: string = '/login',
    showMessage: boolean = false
  ) => {
    console.log('[useAuth] Logout initiated');
    try {
      // Clear auth cookies immediately
      console.log('[useAuth] Clearing auth cookies');
      clearAuthCookies();

      // If user logged in via SSO, call server logout first
      if (currentUser?.loginMethodCode === 'SSO') {
        try {
          console.log('[useAuth] SSO logout detected, calling server logout');
          const res = await logoutMutation({
            loginMethod: currentUser.loginMethodCode,
          }).unwrap();

          // perform local cleanup
          console.log('[useAuth] Performing SSO local cleanup');
          dispatch(setAuthUnauthenticated());
          dispatch(clearApiKey());
          if (showMessage) toast.success('You have been logged out.');
          clearAuthData();
          dispatch(clearUser());
          globalThis.sessionStorage.clear();
          await persistor.purge();
          globalThis.localStorage.removeItem('persist:root');
          globalThis.localStorage.removeItem('envType');

          // If server returned logoutUrl, redirect there
          if (res?.data?.logoutUrl) {
            console.log('[useAuth] Redirecting to SSO logout URL');
            globalThis.location.href = res.data.logoutUrl;
            return;
          }

          // otherwise fallthrough to redirectPath
          console.log('[useAuth] Redirecting to:', redirectPath);
          globalThis.location.href = redirectPath;
          return;
        } catch (err) {
          // If server logout fails, continue to local logout (best effort)
          console.error(
            '[useAuth] SSO logout API failed, proceeding with local logout',
            err
          );
        }
      }

      // Non-SSO or server logout failed -> normal local logout
      console.log('[useAuth] Performing normal local logout');
      dispatch(setAuthUnauthenticated());
      if (showMessage) toast.success('You have been logged out.');
      clearAuthData();
      dispatch(clearUser());
      globalThis.sessionStorage.clear();
      await persistor.purge();
      globalThis.localStorage.removeItem('persist:root');
      globalThis.localStorage.removeItem('envType');

      console.log('[useAuth] Redirecting to login page');
      globalThis.location.href = redirectPath;
    } catch (err) {
      console.error('[useAuth] Logout error:', err);
      globalThis.localStorage.removeItem('envType');
      globalThis.localStorage.clear();
      globalThis.sessionStorage.clear();
      clearAuthCookies();
      dispatch(clearUser());
      dispatch(clearApiKey());
      dispatch(setAuthUnauthenticated());
      globalThis.location.href = redirectPath;
    }
  };

  /**
   * Init auth on app load. Also refetch when a persisted session is
   * authenticated but permissions were never loaded — persist can restore
   * isInitialized: true and skip the first-boot check.
   */
  const needsPermissionRefresh = isAuthenticated && !permissionsLoaded;

  useEffect(() => {
    if (!isInitialized || needsPermissionRefresh) {
      checkAuthStatus().finally(() => {
        dispatch(initializeAuth());
      });
    }
  }, [isInitialized, needsPermissionRefresh, checkAuthStatus, dispatch]);

  return {
    // Actions
    login,
    verifyOtp,
    logout,
    checkAuthStatus,

    // State
    isAuthenticated,
    authStatus,
    isInitialized,
    currentUser,

    // Loading
    isLoading: isLoginLoading || isOtpLoading || isChecking,
    isChecking,

    // Error
    error,

    // Helpers
    needsLogin: ['unauthenticated', 'expired'].includes(authStatus),
  };
};
